import { TDynamicVariable } from "@shared/types";
import { Box, Menu, MenuItem } from "@mui/material";
import React, { FunctionComponent, useState, type Ref } from "react";
import { useDrag, useDrop } from "react-dnd";
import { DynamicVariableInline } from "../DynamicVariableInline/DynamicVariableInline";
import { DVInlineProps } from "../DynamicVariableInline/types";

const DynamicInner: FunctionComponent<
  DVInlineProps & {
    dragAreaId?: string;
    readOnly?: boolean;
  }
> = ({ readOnly, dragAreaId = "", dynamicVariable, ...props }) => {
  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: dragAreaId,
      item: { dynamicVariable, fromDragAreaId: dragAreaId },
      end: (item, monitor) => {},

      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }),
    []
  );
  return (
    <Box
      ref={
        readOnly
          ? undefined
          : (drag as unknown as Ref<HTMLDivElement>)
      }
    >
      <DynamicVariableInline {...props} dynamicVariable={dynamicVariable} />
    </Box>
  );
};

export const DynamicVariableBox: FunctionComponent<
  DVInlineProps & {
    isFullscreen?: boolean;
    dragAreaId: string;
    onChange?: (value: string[]) => void;
    readOnly?: boolean;
    variables?: string[];
    onDrop: (dynamicVariable: TDynamicVariable) => void;
  }
> = ({
  dynamicVariable,
  productId,
  onDrop,
  dragAreaId,
  parentProductId,
  onVariableDelete,
  removeDynamicVariable,
  readOnly,
  activeDynamicVariableId,
  setActiveDynamicVariableId,
  isFullscreen,
  onChange,
  variables,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: [dragAreaId],
    id: dragAreaId,
    drop: (dv: { dynamicVariable: TDynamicVariable }) => {
      onDrop(dv.dynamicVariable);
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    }),
  }));

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onDelete = () => {
    handleClose();
    if (variables) {
      onChange?.(variables.filter((v) => v !== dynamicVariable._id));
    }
  };

  return (
    <Box
      ref={drop as unknown as Ref<HTMLDivElement>}
      key={dynamicVariable._id}
      sx={{
        verticalAlign: "middle",
        minWidth: "unset",
        ...(readOnly ? { pointerEvents: "none" } : {}),
      }}
      onContextMenu={(e: React.MouseEvent<HTMLElement>) => {
        if (isFullscreen) {
          e.preventDefault();
          setAnchorEl(e.currentTarget);
        }
      }}
    >
      <DynamicInner
        dragAreaId={dragAreaId}
        readOnly={readOnly}
        parentProductId={parentProductId}
        productId={productId}
        dynamicVariable={dynamicVariable}
        onVariableDelete={onVariableDelete}
        removeDynamicVariable={removeDynamicVariable}
        activeDynamicVariableId={activeDynamicVariableId}
        setActiveDynamicVariableId={setActiveDynamicVariableId}
        onChange={onChange}
        variables={variables}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        onClose={handleClose}
      >
        <MenuItem onClick={onDelete}>Delete</MenuItem>
      </Menu>
    </Box>
  );
};
